import { Injectable, Inject } from '@angular/core';
import { LogRecord } from '../models/log-record.interface';
import { AuthFailureQueue, AuthFailureQueueItem } from '../models/auth-failure-queue.interface';
import { LoggingConfig, DEFAULT_LOGGING_CONFIG } from '../models/logging-config.interface';
import { environment } from 'src/environments/environment';

/**
 * AuthFailureQueueService implements a FIFO queue for storing logs
 * when authentication failures prevent CloudWatch transmission.
 * 
 * Features:
 * - FIFO (First In, First Out) queue behavior
 * - Configurable maximum size with automatic oldest entry removal
 * - Timestamp tracking for queued log entries
 * - Memory-efficient implementation
 */
@Injectable({
  providedIn: 'root'
})
export class AuthFailureQueueService implements AuthFailureQueue {
  private queue: AuthFailureQueueItem[] = [];
  private readonly maxSize: number;

  constructor() {
    // Get the configured queue size from environment or use default
    const config = (environment as any).logging as LoggingConfig || {};
    this.maxSize = config.authFailureQueueSize ?? DEFAULT_LOGGING_CONFIG.authFailureQueueSize;
  }

  /**
   * Add a log record to the queue
   * If queue is at maximum capacity, removes oldest entry first (FIFO)
   * @param log The log record to enqueue
   */
  enqueue(log: LogRecord): void {
    // If queue is at maximum capacity, remove the oldest entry
    if (this.queue.length >= this.maxSize) {
      this.queue.shift(); // Remove oldest (first) entry
    }

    // Create queue item with timestamp
    const queueItem: AuthFailureQueueItem = {
      logRecord: log,
      queuedAt: Date.now(),
      retryCount: 0
    };

    // Add to end of queue (newest entry)
    this.queue.push(queueItem);
  }

  /**
   * Remove and return the oldest log record from the queue
   * @returns The oldest log record or undefined if queue is empty
   */
  dequeue(): LogRecord | undefined {
    const queueItem = this.queue.shift(); // Remove oldest (first) entry
    return queueItem?.logRecord;
  }

  /**
   * Remove and return all log records from the queue
   * @returns Array of all queued log records in FIFO order
   */
  flush(): LogRecord[] {
    const logRecords = this.queue.map(item => item.logRecord);
    this.queue = []; // Clear the queue
    return logRecords;
  }

  /**
   * Remove all log records from the queue without returning them
   */
  clear(): void {
    this.queue = [];
  }

  /**
   * Get the current number of log records in the queue
   * @returns Number of queued log records
   */
  size(): number {
    return this.queue.length;
  }

  /**
   * Check if the queue is empty
   * @returns True if queue has no log records
   */
  isEmpty(): boolean {
    return this.queue.length === 0;
  }

  /**
   * Check if the queue is at maximum capacity
   * @returns True if queue is at maximum size
   */
  isFull(): boolean {
    return this.queue.length >= this.maxSize;
  }

  /**
   * Get the maximum capacity of the queue
   * @returns Maximum number of log records the queue can hold
   */
  getMaxSize(): number {
    return this.maxSize;
  }

  /**
   * Get queue statistics for debugging and monitoring
   * @returns Object containing queue statistics
   */
  getStatistics(): {
    currentSize: number;
    maxSize: number;
    isEmpty: boolean;
    isFull: boolean;
    oldestEntryAge?: number;
    newestEntryAge?: number;
  } {
    const now = Date.now();
    const stats = {
      currentSize: this.queue.length,
      maxSize: this.maxSize,
      isEmpty: this.isEmpty(),
      isFull: this.isFull(),
      oldestEntryAge: undefined as number | undefined,
      newestEntryAge: undefined as number | undefined
    };

    if (this.queue.length > 0) {
      stats.oldestEntryAge = now - this.queue[0].queuedAt;
      stats.newestEntryAge = now - this.queue[this.queue.length - 1].queuedAt;
    }

    return stats;
  }

  /**
   * Get all queued items with their metadata (for debugging)
   * @returns Array of queue items with timestamps and retry counts
   */
  getQueueItems(): AuthFailureQueueItem[] {
    return [...this.queue]; // Return a copy to prevent external modification
  }

  /**
   * Remove entries older than specified age in milliseconds
   * @param maxAge Maximum age in milliseconds
   * @returns Number of entries removed
   */
  removeOldEntries(maxAge: number): number {
    try {
      const now = Date.now();
      const initialSize = this.queue.length;
      
      this.queue = this.queue.filter(item => (now - item.queuedAt) <= maxAge);
      
      const removedCount = initialSize - this.queue.length;
      
      // Log the operation if any entries were removed
      if (removedCount > 0 && typeof console !== 'undefined') {
        console.log(`[AuthFailureQueue] Removed ${removedCount} old entries (older than ${maxAge}ms)`);
      }
      
      return removedCount;
    } catch (error) {
      // Silent failure with fallback
      if (typeof console !== 'undefined') {
        console.warn('[AuthFailureQueue] Error removing old entries:', error);
      }
      return 0;
    }
  }

  /**
   * Get error message from unknown error type
   */
  private getErrorMessage(error: unknown): string {
    if (error instanceof Error) {
      return error.message;
    }
    if (typeof error === 'string') {
      return error;
    }
    if (error && typeof error === 'object' && 'message' in error) {
      return String((error as any).message);
    }
    return String(error);
  }

  /**
   * Perform queue health check and cleanup
   * @returns Health status and cleanup results
   */
  performHealthCheck(): {
    status: 'healthy' | 'degraded' | 'critical';
    issues: string[];
    cleanupResults?: {
      removedOldEntries: number;
      finalSize: number;
    };
  } {
    const issues: string[] = [];
    let status: 'healthy' | 'degraded' | 'critical' = 'healthy';
    
    try {
      const stats = this.getStatistics();
      
      // Check queue capacity
      if (stats.isFull) {
        issues.push('Queue is at maximum capacity');
        status = 'critical';
      } else if (stats.currentSize > stats.maxSize * 0.8) {
        issues.push('Queue is near capacity (>80%)');
        status = 'degraded';
      }
      
      // Check for old entries
      if (stats.oldestEntryAge && stats.oldestEntryAge > 300000) { // 5 minutes
        issues.push('Queue contains very old entries (>5 minutes)');
        if (status === 'healthy') status = 'degraded';
      }
      
      // Perform cleanup if needed
      let cleanupResults;
      if (status !== 'healthy') {
        const removedOldEntries = this.removeOldEntries(300000); // Remove entries older than 5 minutes
        cleanupResults = {
          removedOldEntries,
          finalSize: this.queue.length
        };
        
        if (removedOldEntries > 0) {
          issues.push(`Cleaned up ${removedOldEntries} old entries`);
        }
      }
      
      return {
        status,
        issues,
        cleanupResults
      };
      
    } catch (error) {
      return {
        status: 'critical',
        issues: [`Health check failed: ${this.getErrorMessage(error)}`]
      };
    }
  }
}